import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { TrendingUp, Plus, X, Loader2, History } from "lucide-react";

function RateModal({ currencies, onClose, onSaved }) {
  const codes = currencies.map((c) => c.code);
  const [form, setForm] = useState({ from_currency: codes[0] || "", to_currency: codes[1] || "", buy_rate: "", sell_rate: "", internal_rate: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.from_currency === form.to_currency) return toast.error("اختر عملتين مختلفتين");
    setBusy(true);
    try {
      await api.post("/rates", {
        from_currency: form.from_currency, to_currency: form.to_currency,
        buy_rate: parseFloat(form.buy_rate), sell_rate: parseFloat(form.sell_rate), internal_rate: parseFloat(form.internal_rate),
      });
      toast.success("تم حفظ سعر الصرف");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md ob-card rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="rate-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold text-white">تحديث سعر صرف</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-zinc-500">من عملة</label>
            <select value={form.from_currency} onChange={(e) => set("from_currency", e.target.value)} data-testid="rate-from"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2 text-white focus:border-[#00E5FF] outline-none">
              {codes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">إلى عملة</label>
            <select value={form.to_currency} onChange={(e) => set("to_currency", e.target.value)} data-testid="rate-to"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2 text-white focus:border-[#00E5FF] outline-none">
              {codes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {[["buy_rate", "سعر الشراء"], ["sell_rate", "سعر البيع"], ["internal_rate", "سعر التحويل الداخلي"]].map(([k, label]) => (
          <div key={k} className="mb-3">
            <label className="text-xs text-zinc-500">{label}</label>
            <input type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} dir="ltr" data-testid={`rate-${k}`}
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2 text-white text-end focus:border-[#00E5FF] outline-none" />
          </div>
        ))}
        <button onClick={save} disabled={busy} data-testid="save-rate-button"
          className="mt-3 w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} حفظ
        </button>
      </div>
    </div>
  );
}

export default function AdminRates() {
  const { t } = useI18n();
  const [rates, setRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    try {
      const [r, c, h] = await Promise.all([api.get("/rates"), api.get("/currencies"), api.get("/rates/history")]);
      setRates(r.data); setCurrencies(c.data); setHistory(h.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><TrendingUp className="text-[#00E5FF]" /> {t("rate.manageTitle")}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t("rate.manageSub")}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowHistory((s) => !s)} data-testid="toggle-history-button"
            className="border border-zinc-700 text-white px-4 py-2.5 rounded-md hover:border-[#00E5FF] hover:text-[#00E5FF] flex items-center gap-2"><History size={16} /> {t("rate.history")}</button>
          <button onClick={() => setShowModal(true)} data-testid="add-rate-button"
            className="bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-md hover:bg-[#33EBFF] flex items-center gap-2"><Plus size={18} /> {t("rate.update")}</button>
        </div>
      </div>

      <div className="ob-card rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="rates-table">
          <thead>
            <tr className="text-zinc-500 text-xs border-b border-zinc-900">
              <th className="text-start px-4 py-3 font-medium">آخر تحديث</th>
              <th className="text-start px-4 py-3 font-medium">التحويل الداخلي</th>
              <th className="text-start px-4 py-3 font-medium">البيع</th>
              <th className="text-start px-4 py-3 font-medium">الشراء</th>
              <th className="text-end px-4 py-3 font-medium">الزوج</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={`${r.from_currency}-${r.to_currency}`} className="border-b border-zinc-900/60 hover:bg-white/[0.02]" data-testid={`rate-row-${r.from_currency}-${r.to_currency}`}>
                <td className="px-4 py-3 text-zinc-500 text-xs" dir="ltr">{r.updated_at ? new Date(r.updated_at).toLocaleString("en-GB") : "—"}</td>
                <td className="px-4 py-3 font-mono-ob text-[#00E5FF]" dir="ltr">{r.internal_rate}</td>
                <td className="px-4 py-3 font-mono-ob text-zinc-300" dir="ltr">{r.sell_rate}</td>
                <td className="px-4 py-3 font-mono-ob text-zinc-300" dir="ltr">{r.buy_rate}</td>
                <td className="px-4 py-3 text-end font-mono-ob text-white font-semibold" dir="ltr">{r.from_currency} → {r.to_currency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showHistory && (
        <div className="ob-card rounded-md p-5" data-testid="rate-history">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><History size={18} /> سجل تغييرات الأسعار</h3>
          {history.length === 0 ? <p className="text-zinc-500 text-sm">لا يوجد سجل بعد.</p> : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-2 border-b border-zinc-900 text-sm">
                  <span className="text-zinc-500 text-xs" dir="ltr">{new Date(h.changed_at).toLocaleString("en-GB")} · {h.changed_by}</span>
                  <span className="font-mono-ob text-zinc-300" dir="ltr">{h.from_currency}→{h.to_currency}: {h.internal_rate}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showModal && <RateModal currencies={currencies} onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}
